/**
 * FILE: clock-skew.service.ts
 * CHỨC NĂNG: Phát hiện đồng hồ / múi giờ máy người dùng lệch so với server và múi giờ doanh nghiệp.
 * TÁC DỤNG: Tin vừa gửi tạm mang giờ máy, các tin khác mang giờ server — máy lệch giờ thì tin xếp sai
 * thứ tự, nhãn "x phút trước" sai. Giờ server lấy từ header Date của response API (ClockSkewInterceptor);
 * lệch quá ngưỡng thì báo ở góc màn hình (một lần mỗi lần tải trang) để người dùng chỉnh lại giờ máy.
 */
import {inject, Injectable} from '@angular/core';
import {TranslocoService} from '@jsverse/transloco';
import {firstValueFrom} from 'rxjs';
import {NotifyService} from '@shared/services/global/notify.service';

/** Lệch từ 1 phút trở lên mới báo — header Date chỉ chính xác tới giây, cộng thời gian truyền mạng. */
const CLOCK_SKEW_WARNING_MS = 60_000;
/** Múi giờ doanh nghiệp mặc định (BusinessI18nService chưa tải cấu hình riêng từ API). */
const BUSINESS_TIMEZONE = 'Asia/Ho_Chi_Minh';
/** Đã báo lệch múi giờ trong phiên trình duyệt này — người dùng ở nước ngoài không bị nhắc mỗi lần tải trang. */
const TIMEZONE_WARNED_KEY = 'vpage_timezone_warned';

@Injectable({providedIn: 'root'})
export class ClockSkewService {
  private readonly notify = inject(NotifyService);
  private readonly transloco = inject(TranslocoService);
  private hasWarnedClockSkew = false;
  private hasCheckedTimezone = false;

  /**
   * Ghi nhận giờ server từ header Date của một response API.
   * @param dateHeader Giá trị header Date (RFC 1123, chính xác tới giây)
   * @param requestStartedAt Thời điểm gửi request theo giờ máy
   * @param responseAt Thời điểm nhận response theo giờ máy
   */
  reportServerDate(dateHeader: string | null, requestStartedAt: number, responseAt: number): void {
    this.checkTimezoneOnce();
    if (!dateHeader || this.hasWarnedClockSkew) return;

    const serverTime = Date.parse(dateHeader);
    if (!Number.isFinite(serverTime)) return;

    // Header Date làm tròn xuống giây → cộng nửa giây; so với giữa lúc gửi và lúc nhận.
    const skewMs = serverTime + 500 - (requestStartedAt + responseAt) / 2;
    if (Math.abs(skewMs) < CLOCK_SKEW_WARNING_MS) return;

    this.hasWarnedClockSkew = true;
    void this.warn('common.clockSkew.title', 'common.clockSkew.message', {
      direction: this.transloco.translate(skewMs > 0 ? 'common.clockSkew.behind' : 'common.clockSkew.ahead'),
      amount: this.formatDuration(Math.abs(skewMs)),
    });
  }

  /** Múi giờ máy khác múi giờ doanh nghiệp thì giờ tin nhắn hiển thị lệch theo múi giờ máy. */
  private checkTimezoneOnce(): void {
    if (this.hasCheckedTimezone) return;
    this.hasCheckedTimezone = true;

    const businessOffset = getTimezoneOffsetMinutes(BUSINESS_TIMEZONE);
    const deviceOffset = -new Date().getTimezoneOffset();
    if (businessOffset === null || businessOffset === deviceOffset) return;
    try {
      if (sessionStorage.getItem(TIMEZONE_WARNED_KEY)) return;
      sessionStorage.setItem(TIMEZONE_WARNED_KEY, '1');
    } catch {
      // WebView/chế độ riêng tư chặn sessionStorage: vẫn báo, mỗi lần tải trang một lần.
    }

    const deviceZone = Intl.DateTimeFormat().resolvedOptions().timeZone;
    void this.warn('common.clockSkew.timezoneTitle', 'common.clockSkew.timezoneMessage', {
      device: `${formatGmtOffset(deviceOffset)}${deviceZone ? ` (${deviceZone})` : ''}`,
      business: `${formatGmtOffset(businessOffset)} (${BUSINESS_TIMEZONE})`,
    });
  }

  /** "12 giờ 40 phút" / "3 phút" */
  private formatDuration(ms: number): string {
    const totalMinutes = Math.round(ms / 60_000);
    const hours = Math.floor(totalMinutes / 60);
    const minutes = totalMinutes % 60;
    return hours > 0
      ? this.transloco.translate('common.clockSkew.hoursMinutes', {hours, minutes})
      : this.transloco.translate('common.clockSkew.minutes', {minutes});
  }

  /** Thông báo ở góc màn hình, giữ tới khi người dùng đóng. Đợi file ngôn ngữ tải xong rồi mới dịch. */
  private async warn(titleKey: string, messageKey: string, params: Record<string, string>): Promise<void> {
    await firstValueFrom(this.transloco.selectTranslation());
    this.notify.showToast({
      type: 'warning',
      title: this.transloco.translate(titleKey),
      html: escapeHtml(this.transloco.translate(messageKey, params)),
      config: {closeButton: true, disableTimeOut: true},
    });
  }
}

/** Độ lệch UTC (phút) của một múi giờ IANA tại thời điểm hiện tại; múi giờ không hợp lệ → null. */
function getTimezoneOffsetMinutes(timeZone: string, date = new Date()): number | null {
  try {
    const parts = new Intl.DateTimeFormat('en-US', {
      timeZone, hourCycle: 'h23',
      year: 'numeric', month: '2-digit', day: '2-digit', hour: '2-digit', minute: '2-digit', second: '2-digit',
    }).formatToParts(date);
    const get = (type: Intl.DateTimeFormatPartTypes) => Number(parts.find(part => part.type === type)?.value);
    const zonedAsUtc = Date.UTC(get('year'), get('month') - 1, get('day'), get('hour'), get('minute'), get('second'));
    return Math.round((zonedAsUtc - date.getTime()) / 60_000);
  } catch {
    return null;
  }
}

/** 420 → "GMT+7", 330 → "GMT+5:30", -480 → "GMT-8" */
function formatGmtOffset(minutes: number): string {
  const sign = minutes < 0 ? '-' : '+';
  const absolute = Math.abs(minutes);
  const rest = absolute % 60;
  return `GMT${sign}${Math.floor(absolute / 60)}${rest ? `:${String(rest).padStart(2, '0')}` : ''}`;
}

function escapeHtml(value: string): string {
  return value.replace(/&/g, '&amp;').replace(/</g, '&lt;').replace(/>/g, '&gt;');
}
